import i18n from "@/plugins/i18n";

/**
 * Defines the process to create a feature
 */
export enum ProcessType {
	/**
	 * This is the process type when the feature
	 * shall not be made. Fill the feature.
	 */
	None = 0,

	/**
	 * Laser cutting
	 */
	Cut = 1,

	/**
	 * Engrave the surface with the cutting laser
	 */
	Engrave = 2,

	/**
	 * Form a screw thread into the sheet metal
	 */
	FormScrewThread = 3,

	/**
	 * Form an extruded thread into the sheet metal
	 */
	FormExtrudedThread = 4,

	/**
	 * Form a countersink into the sheet metal
	 */
	FormCountersink = 5,

	/**
	 * Bend the sheet metal
	 */
	CylindicBend = 6,

	/**
	 * Weld the parts of a sheet metal
	 */
	Weld = 7,

	/**
	 * Drill a hole for a cylindric head bolt.
	 */
	DrillCylinderHeadBoltHole = 8,

	/**
	 * A press nut or anythink that is pressed in.
	 */
	PressJoin = 9,
}

export const getProcessTypeText = (process: ProcessType) => {
	const t = i18n.global.t;
	switch (process) {
		case ProcessType.Cut:
			return t("common.processType.cut");
		case ProcessType.CylindicBend:
			return t("common.processType.cylindicBend");
		case ProcessType.Engrave:
			return t("common.processType.engrave");
		case ProcessType.FormCountersink:
			return t("common.processType.formCountersink");
		case ProcessType.FormExtrudedThread:
			return t("common.processType.formExtrudedThread");
		case ProcessType.FormScrewThread:
			return t("common.processType.formScrewThread");
		case ProcessType.Weld:
			return t("common.processType.weld");
		case ProcessType.DrillCylinderHeadBoltHole:
			return t("common.processType.drillCylinderHeadBoltHole");
		case ProcessType.PressJoin:
			return t("common.processType.pressJoin");
		case ProcessType.None:
			return t("common.processType.none");
		default:
			return t("common.processType.unknown");
	}
};

/**
 * Defines the possible states of a validation message
 * It is possible, that there will be only open messages in the feature
 */
export enum MessageState {
	/**
	 * Message is open and the user has to do something. Or in case of an error,
	 * it prevents the article from being saved.
	 */
	Open = 0,

	/**
	 * Used when a possible screw thread or other feature has been confirmed.
	 * Message is not shown any more.
	 */
	Confirmed = 1,

	/**
	 * A possible feature has been declined. Example: A screw thread is a normal hole.
	 */
	Declined = 2,

	/**
	 * Article has been changed in a way that makes this message obsolete.
	 * Example: Engraving on the back when anchor face has been changed.
	 * Should not be used anymore
	 */
	Obsolete = 3,
}

export enum MessageType {
	ZuKleineLoecher = 0,
	GewindeErkannt = 1,
	DurchzugErkannt = 2,
	GravurErkannt = 3,
	SenkungErkannt = 4,
	InnenAusschnittInBiegezone = 5,
	BiegeradiusUngleichBlechdicke = 6,
	ZuKurzeLasche = 7,
	ZuKleineKonturen = 8,
	BearbeitungNichtMoeglich = 9,
	GravurAufRueckseite = 10,
	BearbeitungAufSchnittkante = 11,
	SchnittkanteNichtSenkrecht = 12,
	HoleErkannt = 13,
	CutoutErkannt = 14,
	DistanzZwischenDurchzugZuKlein = 15,
	BiegungNichtFreigeschnitten = 16,
	AnchorFaceNichtGefunden = 17,
	AbwicklungNichtMöglich = 18,
	ZylinderkopfschraubenLochErkannt = 19,
	FatalerFehler = 99,
	Boost = 100,
	Blexon = 105,
}

export enum BrepType {
	Vertex = 0,
	Edge = 1,
	Face = 2,
}

export enum Schweregrad {
	Info = 0,
	Warnung = 1,
	Error = 2,
}

export enum Surface {
	/**
	 * The screw head is on the upper side in case of counter sink
	 * In case of extruded thread, front means, that the metal goes to this side
	 * A side feature is the anchor face
	 */
	Front = 0,
	/**
	 * The screw head is on the back side
	 */
	Back = 1,

	/**
	 * Defines a side face in case of a side feature
	 */
	Side = 2,
}

export enum LoadingState {
	None = 0,
	Done = 1,
	Loading = 2,
	Saving = 3,
	ChangingAnchorFace = 4,
	SettingFeature = 5,
	MarkFeatures = 6,
}

export enum PluginName {
	Editor = "Editor",
	Bearbeitung = "Bearbeitung",
	Eigenschaften = "Eigenschaften",
	Meldungen = "Meldungen",
	FeatureRecognition = "FeatureRecognition",
	Schliffrichtung = "Schliffrichtung",
	Bauteilinformationen = "Bauteilinformationen",
	Welding = "Welding",
	Features = "Features",
}

export enum EditorMode {
	FeatureMode = 0,
	MeasuringMode = 1,
	RollDirectionMode = 2,
	WeldMode = 3,
}
export const getEditorModeText = (mode: EditorMode) => {
	const t = i18n.global.t;
	switch (mode) {
		case EditorMode.FeatureMode:
			return t("common.editorMode.featureMode");
		case EditorMode.MeasuringMode:
			return t("common.editorMode.measuringMode");
		case EditorMode.RollDirectionMode:
			return t("common.editorMode.rollDirectionMode");
		case EditorMode.WeldMode:
			return t("common.editorMode.weldMode");
		default:
			return t("common.editorMode.none");
	}
};

/**
 * Defines all tasks or functions that can be executed by the backend
 * within the modify feature request.
 * Some other functions are implemented as commands on the cam. Tasks can be
 * combined together and executed in one step and after the tasks, a validation
 * is executed.
 */
export enum TaskType {
	/// <summary>
	/// Should not be used. This marks an undefined task.
	/// </summary>
	Undefined = 0,

	/// <summary>
	/// Changes the anchor face to the opposite site.
	/// </summary>
	SwitchAnchorFace = 1,

	/// <summary>
	/// Creates a countersink from a hole or changes a form to a countersink.
	/// </summary>
	CreateCountersink = 3,

	/// <summary>
	/// Removes the cone of a countersink and creates an engraving instead.
	/// This results in a hole and an engraving feature.
	/// </summary>
	EngraveCountersink = 4,

	/// <summary>
	/// Creates an engraving out of a deepening or anything
	/// that can be engraved
	/// </summary>
	CreateEngraving = 5,

	/// <summary>
	/// Removes the entire feature. Also an engraving is removed.
	/// </summary>
	RemoveFeature = 6,

	/// <summary>
	/// Creates an extruded thread from a hole or changes a form to an extruded thread.
	/// </summary>
	CreateExtrudedThread = 8,

	/// <summary>
	/// Creates a screw thread from a hole or changes a form to an extruded thread.
	/// </summary>
	CreateScrewThread = 10,

	/**
	 * Changes a form of screw thread, countersink or extruded thread into a
	 * normal hole.
	 */
	CreateHole = 12,

	/**
	 * Changes the inner radius of a bend to the specified value
	 */
	ChangeBendRadius = 13,

	/**
	 * Creates release cuts for all bend that are specified by the export ids of their faces
	 */
	CreateReleaseCuts = 14,

	/**
	 * Create a hole for a cylinder head bolt.
	 */
	CreateCylinderheadBoltHole = 15,

	/**
	 * Create a hole for a press join and mark it with the model of a press join
	 */
	CreatePressJoin = 16,
}

/**
 * The main navigation buttons on the sides of the screen.
 * Each corresponds to a panel with various settings.
 */
export enum Panel {
	/**
	 * Editor panel with state changes and general functions and settings
	 */
	Editor = 0,
	/**
	 * Mode panel has dynamic content according to which mode is selected
	 * in the Editor. If in select mode, this panel contains process controls
	 * for creating / changing features.
	 */
	Mode = 1,
	/**
	 * Information about the article.
	 */
	Article = 2,
	/**
	 * Feature tree
	 */
	Features = 3,
	/**
	 * Validation messages.
	 */
	Messages = 4,
	
	/**
	 * Bodies and Structure tree
	 */
	Structure = 5,
}

export enum PressJoinCategory {
	SelfClinchingNuts = 0,
	SelfClinchingThreadedStud = 1,
	SelfClinchingThreadedStandoff = 2,
}
